import React from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/react-dom";
import { ChevronDown } from "lucide-react";
import usePlanningWriteActivity from "../hooks/usePlanningWriteActivity.js";
import usePopoverDismiss from "../hooks/usePopoverDismiss.js";
import PlanningWriteActivityNotice from "./PlanningWriteActivityNotice.jsx";
import { HoverCard } from "./resultsPrimitives/hoverCardPrimitives.jsx";
import AssignmentOptionDetails from "./AssignmentOptionDetails.jsx";
import { tractorOptionDetails, trailerOptionDetails } from "../utils/assignmentOptionDetails.js";
import { loadTractorCatalog, resolveTractorOptionParts, resolveTrailerOptions } from "../lib/tractorAssignmentsApi.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";

const NO_TRACTOR_OPTION_ID = "__none__";

function TractorOptionColumns({ name, usedByLabel = "", muted = false, selected = false }) {
  return <>
    <span className={`min-w-0 flex-1 truncate ${muted ? "text-content-muted" : ""}`}>{name}</span>
    {usedByLabel ? <span className={`shrink-0 text-right tabular-nums ${selected ? "" : "text-content-muted"}`}>{usedByLabel}</span> : null}
  </>;
}

// Liste du camion d'une remorque (ou des remorques, avec options) pour la date et la période affichées.
// Même ergonomie que le choix du chauffeur.
export function TractorSelect({ catalog, disabled = false, error = "", onChange, truckId = "", value = "", options: listOptions = null,
  ariaLabel = "Camion", loadingLabel = "Chargement des camions…", placeholder = "" }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const triggerRef = React.useRef(null);
  const listId = React.useId();
  const lines = catalog?.lines || [];
  const options = React.useMemo(() => listOptions || [
    { id: NO_TRACTOR_OPTION_ID, name: "Sans camion", usedByLabel: "" },
    ...(catalog?.tractors || []).map((tractor) => ({
      id: tractor.tractor_truck_id,
      ...resolveTractorOptionParts(tractor, { lines, truckId }),
      details: tractor,
    })),
  ], [catalog, lines, listOptions, truckId]);
  const selected = options.find((option) => option.id === (value || NO_TRACTOR_OPTION_ID)) || (listOptions ? null : options[0]);
  const selectedIndex = Math.max(0, options.findIndex((option) => option.id === selected?.id));
  const [highlightedIndex, setHighlightedIndex] = React.useState(selectedIndex);
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: isOpen,
    strategy: "fixed",
    placement: "bottom-start",
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, { width: `${Math.round(rects.reference.width)}px` });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const close = React.useCallback(() => setIsOpen(false), []);
  usePopoverDismiss({ anchorElement: triggerRef.current, floatingRef: refs.floating, isOpen, onClose: close });
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      close();
    };
    document.addEventListener("keydown", onEscape, true);
    return () => document.removeEventListener("keydown", onEscape, true);
  }, [close, isOpen]);
  React.useEffect(() => {
    if (isOpen) setHighlightedIndex(selectedIndex);
  }, [isOpen, selectedIndex]);
  const choose = (option) => {
    if (!option) return;
    onChange?.(option.id === NO_TRACTOR_OPTION_ID ? null : option.id);
    close();
  };
  const triggerLabel = !catalog ? (error ? "État indisponible" : loadingLabel) : selected?.name || placeholder;
  const portalRoot = getReactPortalRoot();
  return <div className="grid min-w-0 gap-1">
    <button
      ref={(element) => { triggerRef.current = element; refs.setReference(element); }}
      type="button"
      aria-controls={listId}
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-label={ariaLabel}
      data-tractor-select
      className="flex h-8 w-full min-w-0 items-center gap-2 rounded-[4px] border border-line-soft bg-white px-2 text-left text-xs text-content-ink focus:border-accent focus:outline-none disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
      disabled={disabled || !catalog}
      onClick={() => setIsOpen((current) => !current)}
      onKeyDown={(event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          if (!isOpen) setIsOpen(true);
          else setHighlightedIndex((current) => (current + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length);
        } else if (event.key === "Enter" && isOpen) {
          event.preventDefault();
          choose(options[highlightedIndex]);
        }
      }}
    >
      <TractorOptionColumns name={triggerLabel} usedByLabel={catalog ? selected?.usedByLabel : ""} muted={!catalog || !selected} />
      <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-content-muted" />
    </button>
    {isOpen && portalRoot ? createPortal(
      <ul
        ref={refs.setFloating}
        id={listId}
        role="listbox"
        aria-label={ariaLabel}
        data-tractor-select-list="true"
        className="z-popover max-h-64 overflow-y-auto rounded-[4px] border border-line-soft bg-white py-1 shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
        style={{ ...floatingStyles, visibility: isPositioned ? "visible" : "hidden", zIndex: 2200 }}
      >
        {options.map((option, index) => {
          const isSelected = option.id === selected?.id;
          const optionButton = <button
              type="button"
              role="option"
              aria-selected={isSelected}
              className={[
                "flex w-full items-center gap-3 px-2 py-1 text-left text-xs hover:bg-slate-50",
                isSelected ? "bg-accent text-white hover:bg-accent-strong" : "",
                index === highlightedIndex && !isSelected ? "bg-slate-50" : "",
              ].filter(Boolean).join(" ")}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => choose(option)}
            >
              <TractorOptionColumns name={option.name} usedByLabel={option.usedByLabel} selected={isSelected} />
            </button>;
          return <li key={option.id} role="presentation">
            {option.details ? <HoverCard referenceAsChild closeOnReferenceClick closeOnScroll placement="right"
              title={option.name} content={<AssignmentOptionDetails rows={listOptions
                ? trailerOptionDetails(option.details) : tractorOptionDetails(option.details)} />}>
              {optionButton}
            </HoverCard> : optionButton}
          </li>;
        })}
      </ul>,
      portalRoot,
    ) : null}
  </div>;
}

export default function TractorAssignmentEditor({ truckId, dispatchDate = "", dispatchPeriod = "day", onCommit }) {
  const planningWriteBlocked = Boolean(usePlanningWriteActivity());
  const [catalog, setCatalog] = React.useState(null);
  const [error, setError] = React.useState("");
  const [selection, setSelection] = React.useState(undefined);
  const [busy, setBusy] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  React.useEffect(() => {
    const controller = new AbortController();
    setError("");
    loadTractorCatalog({ dispatchDate, dispatchPeriod, signal: controller.signal })
      .then(setCatalog)
      .catch((failure) => { if (failure.name !== "AbortError") setError(failure.message); });
    return () => controller.abort();
  }, [dispatchDate, dispatchPeriod, refreshKey]);
  const line = catalog?.lines?.find((item) => item.truck_id === truckId) || null;
  const currentId = line?.tractor_status === "assigned" ? line.tractor_truck_id : "";
  const selectedId = selection === undefined ? currentId : selection || "";
  const submit = async () => {
    if (planningWriteBlocked || !line) return;
    setBusy(true);
    try {
      const result = await onCommit?.({ tractorTruckId: selectedId || null, expectedVersion: line.version, dispatchPeriod, truckId });
      if (result) {
        // Le chauffeur suit son camion : la vue Chauffeurs du Data Manager se rafraîchit.
        window.dispatchEvent(new CustomEvent("grain-driver-assignments-changed", { detail: { scope: "dated", truckIds: result.modified_truck_ids || [] } }));
        setSelection(undefined);
      }
    } finally {
      setBusy(false);
      setRefreshKey((value) => value + 1);
    }
  };
  return <div className="grid gap-2 rounded-[4px] border border-line-soft bg-white p-2" data-tractor-assignment-editor>
    <TractorSelect catalog={catalog} disabled={busy} error={error} truckId={truckId} value={selectedId}
      onChange={(tractorId) => setSelection(tractorId || "")} />
    {!busy ? <PlanningWriteActivityNotice /> : null}
    <div className="flex items-center gap-2">
      <button type="button"
        className="h-7 rounded-[4px] border border-accent bg-accent px-2 text-xs font-semibold text-white hover:border-accent-strong hover:bg-accent-strong disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none"
        disabled={busy || planningWriteBlocked || selection === undefined || selectedId === currentId || !line}
        onClick={() => void submit()}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
    </div>
    {error ? <p role="alert" className="text-xs text-red-700">{error}</p> : null}
  </div>;
}

// « Changer de remorque » : le camion de cette remorque part, avec son chauffeur, sur une autre remorque de la période.
export function TrailerAssignmentEditor({ truckId, dispatchDate = "", dispatchPeriod = "day", onCommit }) {
  const planningWriteBlocked = Boolean(usePlanningWriteActivity());
  const [catalog, setCatalog] = React.useState(null);
  const [error, setError] = React.useState("");
  const [targetId, setTargetId] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  React.useEffect(() => {
    const controller = new AbortController();
    setError("");
    loadTractorCatalog({ dispatchDate, dispatchPeriod, signal: controller.signal })
      .then(setCatalog)
      .catch((failure) => { if (failure.name !== "AbortError") setError(failure.message); });
    return () => controller.abort();
  }, [dispatchDate, dispatchPeriod, refreshKey]);
  const line = catalog?.lines?.find((item) => item.truck_id === truckId) || null;
  const options = React.useMemo(() => resolveTrailerOptions(catalog, truckId), [catalog, truckId]);
  const target = options.find((option) => option.id === targetId) || null;
  const submit = async () => {
    if (planningWriteBlocked || !target || line?.tractor_status !== "assigned") return;
    setBusy(true);
    try {
      const result = await onCommit?.({ mode: "trailer", truckId: target.id, tractorTruckId: line.tractor_truck_id,
        expectedVersion: target.version, holderTruckId: truckId, dispatchPeriod });
      if (result) {
        window.dispatchEvent(new CustomEvent("grain-driver-assignments-changed", { detail: { scope: "dated", truckIds: result.modified_truck_ids || [] } }));
        setTargetId("");
      }
    } finally {
      setBusy(false);
      setRefreshKey((value) => value + 1);
    }
  };
  return <div className="grid gap-2 rounded-[4px] border border-line-soft bg-white p-2" data-trailer-assignment-editor>
    <TractorSelect catalog={catalog} disabled={busy} error={error} options={options} value={targetId} ariaLabel="Remorque"
      loadingLabel="Chargement des remorques…" placeholder="Choisir une remorque" onChange={(id) => setTargetId(id || "")} />
    {!busy ? <PlanningWriteActivityNotice /> : null}
    <div className="flex items-center gap-2">
      <button type="button"
        className="h-7 rounded-[4px] border border-accent bg-accent px-2 text-xs font-semibold text-white hover:border-accent-strong hover:bg-accent-strong disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none"
        disabled={busy || planningWriteBlocked || !target || line?.tractor_status !== "assigned"}
        onClick={() => void submit()}>{busy ? "Enregistrement…" : "Changer de remorque"}</button>
    </div>
    {error ? <p role="alert" className="text-xs text-red-700">{error}</p> : null}
  </div>;
}
